// =============================================================================
// Płótno edytora — rysuje układ w skali i pozwala go przeciągać.
//
// PŁÓTNO NIE JEST PODGLĄDEM I NIE UDAJE, ŻE JEST. To jest widok DO MANIPULACJI:
// prostokąty, uchwyty, zaznaczenie. Prawdę o tym, co wyjdzie z drukarki, mówi
// panel obok, bo renderuje TĄ SAMĄ ścieżką co prawdziwy wydruk (`…/podglad`).
//
// Rozdział jest celowy. Gdyby płótno próbowało rysować prawdziwy kod kreskowy
// i prawdziwe łamanie tekstu, powstałby drugi renderer — a dwa renderery
// rozjeżdżają się zawsze, tylko nie wiadomo kiedy. Rozjazd objawiłby się jako
// „na ekranie było dobrze, a wydrukowało się inaczej", czyli dokładnie ta
// klasa błędów, której cała ta architektura ma nie mieć. Dlatego kod kreskowy
// na płótnie to prostokąt z etykietą symboliki — czytelny obrys do przesuwania,
// bez udawania wierności.
//
// MILIMETR JEST JEDNOSTKĄ MODELU. Piksel powstaje wyłącznie tutaj, przez
// mnożenie przez `skala`. Żadna wartość w pikselach nie wraca do układu.
// =============================================================================

import type { LayoutNode, PrintLayout, Wyrazenie } from '@insolitum/print-templates';
import { FONT } from '@insolitum/print-templates/uklad';
import { cn } from '@insolitum/ui';
import { useCallback, useRef, useState } from 'react';

import { przyciagnij, przycinajDoObszaru, pudelkoTekstu } from './stan';

/** Milimetry płótna dla mediów wzoru. Dokument ma format, etykieta — wymiary. */
export function wymiaryMm(media: PrintLayout['media']): { szer: number; wys: number } {
  if (media.kind === 'label') return { szer: media.widthMm, wys: media.heightMm };
  switch (media.page) {
    case 'A4':
      return { szer: 210, wys: 297 };
    case 'A5':
      return { szer: 148, wys: 210 };
    case 'receipt80mm':
      // Paragon jest rolką — wysokość zależy od treści. 200 mm to płótno
      // robocze, nie deklaracja formatu; enkoder i tak tnie po treści.
      return { szer: 80, wys: 200 };
  }
}

/**
 * Podgląd wyrażenia jako tekst: literały dosłownie, wiązania w nawiasach.
 *
 * Chwila wydruku dostaje PRZYKŁADOWĄ wartość, a nie nazwę tokena: `{data}`
 * na płótnie zajmowałoby sześć znaków, a wydruk dziesięć — czyli blok
 * mieszczący się w edytorze i ucinany przez drukarkę. Data jest przykładowa
 * i celowo nie jest dzisiejsza, żeby nikt nie wziął płótna za podgląd.
 */
export function podglądWyrazenia(w: Wyrazenie): string {
  return w
    .map((t) => {
      if ('lit' in t) return t.lit;
      if ('var' in t) return `{${t.var}}`;
      if ('teraz' in t) return PRZYKLAD_TERAZ[t.teraz] ?? '{chwila}';
      return `{${t.pole}}`;
    })
    .join('');
}

/** Przykładowa chwila na płótnie — ta sama długość, co format wydruku. */
const PRZYKLAD_TERAZ: Record<string, string> = {
  data: '31.12.2026',
  godzina: '23:59',
  data_godzina: '31.12.2026 23:59',
};

interface PlotnoProps {
  layout: PrintLayout;
  zaznaczony: string | null;
  onZaznacz: (id: string | null) => void;
  onPrzesun: (id: string, dxMm: number, dyMm: number) => void;
  /** px na milimetr. Steruje nim suwak powiększenia w pasku narzędzi. */
  skala: number;
}

export function Plotno({ layout, zaznaczony, onZaznacz, onPrzesun, skala }: PlotnoProps) {
  const { szer, wys } = wymiaryMm(layout.media);
  const siatkaMm = layout.edytor?.siatkaMm ?? 1;
  const przyciaganie = layout.edytor?.przyciaganie ?? true;
  const ref = useRef<HTMLDivElement>(null);
  const [ciagniety, setCiagniety] = useState<string | null>(null);

  // Punkt chwytu w mm — przesunięcie liczymy od NIEGO, a nie od rogu węzła.
  // Inaczej węzeł skakałby środkiem pod kursor przy pierwszym ruchu.
  const chwyt = useRef<{ xMm: number; yMm: number } | null>(null);

  const naMm = useCallback(
    (e: { clientX: number; clientY: number }) => {
      const box = ref.current?.getBoundingClientRect();
      if (!box) return { xMm: 0, yMm: 0 };
      return { xMm: (e.clientX - box.left) / skala, yMm: (e.clientY - box.top) / skala };
    },
    [skala]
  );

  const start = (e: React.PointerEvent, node: LayoutNode) => {
    e.stopPropagation();
    onZaznacz(node.id);
    if (node.zablokowany) return;
    chwyt.current = naMm(e);
    setCiagniety(node.id);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const ruch = (e: React.PointerEvent) => {
    if (!ciagniety || !chwyt.current) return;
    const wleczony = layout.nodes.find((n) => n.id === ciagniety);
    if (!wleczony) return;
    const teraz = naMm(e);
    const przyciagniety = {
      dx: przyciagnij(teraz.xMm - chwyt.current.xMm, siatkaMm, przyciaganie),
      dy: przyciagnij(teraz.yMm - chwyt.current.yMm, siatkaMm, przyciaganie),
    };
    // Przycięcie do etykiety PRZED oddaniem do modelu — inaczej mysz
    // wyprowadza blok poza obszar, autozapis odbija się 422-ką `poza_mediami`
    // i praca staje. Kolejność jest istotna: najpierw siatka, potem krawędź,
    // bo przyciąganie wykonane po przycięciu potrafiłoby wypchnąć blok
    // z powrotem za krawędź o pół oczka siatki.
    const { dx, dy } = przycinajDoObszaru(
      wleczony,
      layout.media,
      przyciagniety.dx,
      przyciagniety.dy
    );
    if (dx === 0 && dy === 0) return;
    // Chwyt przesuwamy o TYLE, ile realnie oddaliśmy do modelu. Bez tego
    // przyciąganie gubiłoby resztę i węzeł zostawałby w tyle za kursorem.
    chwyt.current = { xMm: chwyt.current.xMm + dx, yMm: chwyt.current.yMm + dy };
    onPrzesun(ciagniety, dx, dy);
  };

  const koniec = () => {
    setCiagniety(null);
    chwyt.current = null;
  };

  return (
    // BEZ własnego `overflow`: przewija KONTENER edytora, jeden na całą
    // kolumnę. Dwa zagnieżdżone obszary przewijania dawały dwa paski obok
    // siebie i płótno, które przy powiększeniu uciekało w tym wewnętrznym.
    <div className="w-max min-w-full" onPointerDown={() => onZaznacz(null)}>
      <div
        ref={ref}
        className="relative mx-auto bg-white shadow-lg"
        style={{
          width: szer * skala,
          height: wys * skala,
          // Siatka rysowana tłem, nie elementami: setka divów co milimetr
          // zabijała płynność przeciągania na A4.
          backgroundImage: przyciaganie
            ? `linear-gradient(to right, rgba(0,0,0,.06) 1px, transparent 1px),
               linear-gradient(to bottom, rgba(0,0,0,.06) 1px, transparent 1px)`
            : undefined,
          backgroundSize: `${siatkaMm * skala}px ${siatkaMm * skala}px`,
        }}
        onPointerMove={ruch}
        onPointerUp={koniec}
        onPointerCancel={koniec}
      >
        {layout.nodes.map((node) => (
          <WezelNaPlotnie
            key={node.id}
            node={node}
            skala={skala}
            zaznaczony={zaznaczony === node.id}
            onStart={start}
          />
        ))}
      </div>
    </div>
  );
}

function WezelNaPlotnie({
  node,
  skala,
  zaznaczony,
  onStart,
}: {
  node: LayoutNode;
  skala: number;
  zaznaczony: boolean;
  onStart: (e: React.PointerEvent, n: LayoutNode) => void;
}) {
  // Ukryty węzeł NIE trafia do IR, więc nie drukuje się i nie ma go w
  // podglądzie. Na płótnie zostaje — półprzezroczysty — bo inaczej nie dałoby
  // się go zaznaczyć, żeby odkryć z powrotem.
  const wspolne = {
    position: 'absolute' as const,
    opacity: node.ukryty ? 0.35 : 1,
    cursor: node.zablokowany ? 'not-allowed' : 'move',
    outline: zaznaczony ? '2px solid rgb(56,132,255)' : undefined,
    outlineOffset: 1,
  };
  const start = (e: React.PointerEvent) => onStart(e, node);

  switch (node.kind) {
    case 'text': {
      // Pudełko liczy `stan.ts` z `obrysWezla` — to ten sam prostokąt, który
      // mierzy walidator i którym przycinamy przeciąganie.
      const p = pudelkoTekstu(node, skala);
      return (
        <div
          onPointerDown={start}
          style={{
            ...wspolne,
            left: p.left,
            top: p.top,
            width: p.width,
            fontSize: p.fontSize,
            // Ta sama interlinia, co w facsimile (`line-height: 1.15`) i co
            // w kroku wiersza enkodera ZPL. Bez niej blok zawijający się na
            // cztery wiersze miałby na płótnie inną wysokość niż na wydruku,
            // choć jego szerokość zgadzałaby się co do milimetra.
            lineHeight: 1.15,
            fontFamily: FONT[node.kroj ?? 'ui'],
            fontWeight: node.bold ? 700 : 400,
            // `maxWidthMm` to blok tekstu — zawija (facsimile stawia tam
            // `white-space: normal`). Bez bloku napis jest jedną linią.
            whiteSpace: node.maxWidthMm ? 'normal' : 'nowrap',
            color: '#111',
            textAlign: node.align ?? 'left',
          }}
        >
          {podglądWyrazenia(node.tekst) || ' '}
        </div>
      );
    }

    case 'barcode':
      return (
        <div
          onPointerDown={start}
          style={{
            ...wspolne,
            left: node.xMm * skala,
            top: node.yMm * skala,
            height: node.heightMm * skala,
            minWidth: 30 * skala,
            // Paski są dekoracją obrysu, nie kodem — prawdziwy kod rysuje
            // enkoder w podglądzie. Gdyby tu stał czytelny kod, ktoś kiedyś
            // zeskanowałby go z ekranu i uwierzył, że to jest ten sam.
            background: 'repeating-linear-gradient(90deg,#111 0 2px,transparent 2px 4px)',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
          }}
          title={`${node.symbology}: ${podglądWyrazenia(node.dane)}`}
        >
          {node.showText !== false && (
            <span
              style={{ fontSize: 8, background: '#fff', padding: '0 2px', whiteSpace: 'nowrap' }}
            >
              {podglądWyrazenia(node.dane)}
            </span>
          )}
        </div>
      );

    case 'qr':
      return (
        <div
          onPointerDown={start}
          style={{
            ...wspolne,
            left: node.xMm * skala,
            top: node.yMm * skala,
            width: node.sizeMm * skala,
            height: node.sizeMm * skala,
            background: 'repeating-conic-gradient(#111 0 25%, #fff 0 50%) 0 0/25% 25%',
          }}
          title={podglądWyrazenia(node.dane)}
        />
      );

    case 'line':
      return (
        <div
          onPointerDown={start}
          style={{
            ...wspolne,
            left: Math.min(node.xMm, node.x2Mm) * skala,
            top: Math.min(node.yMm, node.y2Mm) * skala,
            width: Math.max(Math.abs(node.x2Mm - node.xMm) * skala, 1),
            height: Math.max(
              Math.abs(node.y2Mm - node.yMm) * skala,
              (node.thicknessMm ?? 0.3) * skala
            ),
            background: '#111',
          }}
        />
      );

    case 'box':
      return (
        <div
          onPointerDown={start}
          style={{
            ...wspolne,
            left: node.xMm * skala,
            top: node.yMm * skala,
            width: node.widthMm * skala,
            height: node.heightMm * skala,
            border: `${Math.max((node.thicknessMm ?? 0.3) * skala, 1)}px solid #111`,
          }}
        />
      );

    case 'image':
      return (
        <div
          onPointerDown={start}
          className="flex items-center justify-center text-[9px] text-aurora-subtle"
          style={{
            ...wspolne,
            left: node.xMm * skala,
            top: node.yMm * skala,
            width: node.widthMm * skala,
            height: node.heightMm * skala,
            border: '1px dashed #94a3b8',
            background: '#f8fafc',
          }}
          title={node.alt ?? ''}
        >
          {node.zrodlo.rodzaj === 'logo_organizacji'
            ? 'Logo'
            : node.zrodlo.rodzaj === 'zmienna'
              ? `{${node.zrodlo.var}}`
              : 'Zasób'}
        </div>
      );

    case 'repeat':
      return (
        <div
          onPointerDown={start}
          className={cn('flex items-center text-[9px] text-sky-700')}
          style={{
            ...wspolne,
            left: 0,
            top: node.yMm * skala,
            right: 0,
            height: Math.max(node.stepMm * skala, 12),
            border: '1px dashed rgb(2,132,199)',
            background: 'rgba(2,132,199,.06)',
            paddingLeft: 4,
          }}
        >
          {node.zrodlo.rodzaj === 'lista'
            ? `Powtórzenie: {${node.zrodlo.var}} × ${node.zrodlo.maxWierszy}`
            : node.zrodlo.rodzaj === 'zawijanie'
              ? `Tekst łamany: ${podglądWyrazenia(node.zrodlo.tekst)}`
              : `Powtórzenie: ${node.zrodlo.wiersze.length} wierszy`}
        </div>
      );
  }
}
